import { getToken, clearToken } from "./auth";

const BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Preview-Generierungs-Settings pro Buch. Leerer String = Parameter wird NICHT
 * gesendet (API-Default gilt). Der Server füllt fehlende Felder immer mit den
 * Defaults, die GET liefert also stets ein vollständiges Objekt.
 */
/** Position und Aussehen des Kindernamens auf dem fertigen Preview. */
export interface PreviewNameLayout {
  prefixText: string;
  fontSizePercent: number;
  prefixSizeRatio: number;
  gapPercent: number;
  centerXPercent: number;
  /** Abstand der Grundlinie vom unteren Bildrand, in % der Bildhöhe. */
  bottomOffsetPercent: number;
  maxWidthPercent: number;
  color: string;
  shadowEnabled: boolean;
  shadowBlurPercent: number;
  shadowOpacity: number;
}

export interface PreviewSettings {
  provider: "gemini" | "openai";
  nameLayout: PreviewNameLayout;
  timeoutMs: number | null;
  gemini: {
    model: string;
    /** "auto" = Seitenverhältnis aus dem Cover ableiten. */
    aspectRatio: string;
    imageSize: string;
    thinkingLevel: "LOW" | "MEDIUM" | "HIGH";
  };
  openai: OpenAiImageParams;
}

/**
 * Modellparameter von POST /v1/images/edits. Identisch für Preview- und
 * Szenen-Generierung — beide sprechen denselben Endpoint an.
 */
export interface OpenAiImageParams {
  model: string;
  /** "auto" oder WIDTHxHEIGHT. */
  size: string;
  quality: "" | "auto" | "low" | "medium" | "high" | "xhigh" | "max";
  background: "" | "auto" | "transparent" | "opaque";
  inputFidelity: "" | "low" | "high";
  outputFormat: "" | "png" | "jpeg" | "webp";
  /** 0–100, nur für jpeg/webp. */
  outputCompression: string;
  moderation: "" | "auto" | "low";
  n: string;
  /** Roh-Passthrough für Parameter, die hier (noch) nicht modelliert sind. */
  extra: Record<string, string>;
}

/**
 * Von der Live-API bestätigte Werte für POST /v1/images/edits.
 * "" = Parameter wird nicht gesendet (API-Default gilt).
 * Ein `effort`/`reasoning_effort` gibt es bei diesem Endpoint NICHT.
 *
 * Zwei Parameter sind bedingt und werden serverseitig weggelassen, wenn sie
 * nicht passen: `input_fidelity` (nur die
 * gpt-image-1-Familie) und `output_compression` (nur bei jpeg/webp).
 */
export function modelSupportsInputFidelity(model: string): boolean {
  return /^gpt-image-1\b/.test(model.trim());
}
export function formatSupportsCompression(outputFormat: string): boolean {
  return outputFormat === "jpeg" || outputFormat === "webp";
}
export const OPENAI_VALUES = {
  quality: ["", "auto", "low", "medium", "high", "xhigh", "max"],
  background: ["", "auto", "transparent", "opaque"],
  inputFidelity: ["", "low", "high"],
  outputFormat: ["", "png", "jpeg", "webp"],
  moderation: ["", "auto", "low"],
  size: ["auto", "1024x1024", "1536x1024", "1024x1536", "1824x1024", "1024x1824"],
  model: ["gpt-image-2.6-flare", "gpt-image-2.5-flare", "gpt-image-2.5-sunburst", "gpt-image-2", "gpt-image-1"],
} as const;

export const GEMINI_VALUES = {
  aspectRatio: ["auto", "1:1", "2:3", "3:2", "3:4", "4:3", "4:5", "5:4", "9:16", "16:9", "21:9"],
  imageSize: ["1K", "2K", "4K"],
  thinkingLevel: ["LOW", "MEDIUM", "HIGH"],
} as const;

export const DEFAULT_NAME_LAYOUT: PreviewNameLayout = {
  prefixText: "mit",
  fontSizePercent: 9,
  prefixSizeRatio: 0.62,
  gapPercent: 25,
  centerXPercent: 50,
  bottomOffsetPercent: 9,
  maxWidthPercent: 80,
  color: "#ffffff",
  shadowEnabled: true,
  shadowBlurPercent: 0.6,
  shadowOpacity: 0.55,
};

export const DEFAULT_PREVIEW_SETTINGS: PreviewSettings = {
  provider: "gemini",
  nameLayout: DEFAULT_NAME_LAYOUT,
  timeoutMs: null,
  gemini: {
    model: "gemini-3.1-flash-image-preview",
    aspectRatio: "auto",
    imageSize: "1K",
    thinkingLevel: "HIGH",
  },
  openai: {
    model: "gpt-image-2.6-flare",
    size: "auto",
    quality: "xhigh",
    background: "",
    inputFidelity: "",
    outputFormat: "",
    outputCompression: "",
    moderation: "",
    n: "1",
    extra: {},
  },
};

/**
 * Settings der Szenen-Generierung ("Character Replacement") pro Buch: aus der
 * Ursprungszene (@img1) + dem Charakter-Sheet (@img2) wird die fertige
 * Buchseite. Läuft nach Bestellung automatisch. Gleiche OpenAI-Parameter wie
 * die Preview-Generierung — beide gehen an POST /v1/images/edits.
 */
export interface SceneSettings {
  provider: "gemini" | "openai";
  /** Timeout einer einzelnen Szene in ms. null = globaler Timeout. */
  timeoutMs: number | null;
  gemini: {
    model: string;
    aspectRatio: string;
    imageSize: string;
    thinkingLevel: "LOW" | "MEDIUM" | "HIGH";
  };
  openai: OpenAiImageParams;
}

/** Deep-Partial der SceneSettings, pro Szene abweichend gespeichert. */
export interface SceneOverrides {
  provider?: "gemini" | "openai";
  timeoutMs?: number | null;
  gemini?: Partial<SceneSettings["gemini"]>;
  openai?: Partial<OpenAiImageParams>;
}

export const SCENE_GEMINI_VALUES = {
  aspectRatio: ["1:1", "2:3", "3:2", "3:4", "4:3", "4:5", "5:4", "9:16", "16:9", "21:9"],
  imageSize: ["1K", "2K", "4K"],
  thinkingLevel: ["LOW", "MEDIUM", "HIGH"],
} as const;

export const DEFAULT_SCENE_SETTINGS: SceneSettings = {
  provider: "openai",
  timeoutMs: 300000,
  gemini: {
    model: "gemini-3.1-flash-image-preview",
    aspectRatio: "21:9",
    imageSize: "2K",
    thinkingLevel: "HIGH",
  },
  openai: {
    model: "gpt-image-2.5-sunburst",
    size: "auto",
    quality: "max",
    background: "",
    inputFidelity: "",
    outputFormat: "",
    outputCompression: "",
    moderation: "",
    n: "1",
    extra: {},
  },
};

export type PreviewAssetKind =
  | "cover"
  | "overlay"
  | "placeholder"
  | "font-name"
  | "font-prefix"
  | "master-sheet";

export interface Book {
  id: string;
  slug: string;
  title: string;
  sceneCount: number;
  basePath: string;
  shopifyProductHandles: string[];
  /** Cover illustration used as @img1 of the customer preview (null = keins). */
  previewCoverPath: string | null;
  /** Transparentes Overlay, das nach der Generierung draufgelegt wird. */
  previewOverlayPath: string | null;
  /** Platzhalter-Bild, das während der Generierung ausgeliefert wird. */
  previewPlaceholderPath: string | null;
  /** Master-Sheet des Character-Sheets (Outfit steckt drin); null = statisches Standard-Master. */
  characterMasterSheetPath: string | null;
  /** Schrift für den Kindernamen (.ttf/.otf). */
  previewNameFontPath: string | null;
  /** Schrift für das Wort vor dem Namen ("mit"). */
  previewPrefixFontPath: string | null;
  /** Generierungs-Settings des Kunden-Previews (Server liefert immer komplett). */
  previewSettings: PreviewSettings;
  /** Settings der Szenen-Generierung (Server liefert immer komplett). */
  sceneSettings: SceneSettings;
  /** Legacy-Gemini-Spalten; nur noch Fallback, solange sceneSettings NULL ist. */
  geminiModel: string;
  geminiAspectRatio: string;
  geminiImageSize: string;
  geminiThinkingLevel: "LOW" | "MEDIUM" | "HIGH";
  createdAt: string;
  updatedAt: string;
}

export interface BookScene {
  id: string;
  bookId: string;
  index: number;
  name: string;
  csvColumn: string;
  kind: "generated" | "fixed";
  prompt: string | null;
  sceneEmotion: string | null;
  baseImagePath: string | null;
  fixedImagePath: string | null;
  /** Legacy: alte Gemini-Overrides; werden in sceneOverrides.gemini gespiegelt. */
  geminiOverrides: Record<string, unknown> | null;
  sceneOverrides: SceneOverrides | null;
  orderIndex: number;
  enabled: boolean;
  /** Exklusive Szene von „Outfit vom Foto“ — nur für diese Bestellungen. */
  photoOutfitOnly: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BookTitle {
  titleNumber: string;
  bookId: string;
  binding: "SC" | "HC";
  status: "draft" | "mastering_uploaded" | "tsa_pending" | "live" | "rejected";
  heightMm: number | null;
  widthMm: number | null;
  totalExtent: number | null;
  colourPages: number | null;
  colourPagesPosition: string | null;
  coverPdfPath: string | null;
  bookblockPdfPath: string | null;
  masteringXmlPath: string | null;
  tsaReceivedAt: string | null;
  liveAt: string | null;
  createdAt: string;
}

export interface BookDetail {
  book: Book;
  scenes: BookScene[];
  titles: BookTitle[];
}

async function authed(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  return res;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authed(path);
  if (!res.ok) throw new Error(`request failed: ${res.status}`);
  return res.json();
}

async function send<T>(path: string, method: string, body: unknown): Promise<T> {
  const res = await authed(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`request failed: ${res.status} ${text}`);
  }
  return res.json();
}

async function del(path: string): Promise<void> {
  const res = await authed(path, { method: "DELETE" });
  if (!res.ok) {
    let message = `delete failed: ${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch { /* ignore parse failures */ }
    throw new Error(message);
  }
}

export const booksApi = {
  list: () => getJson<{ books: Book[] }>(`/api/books`),
  get: (id: string) => getJson<BookDetail>(`/api/books/${id}`),
  create: (body: {
    slug: string;
    title: string;
    scene_count?: number;
    base_path?: string;
    shopify_product_handles?: string[];
  }) => send<{ book: Book }>(`/api/books`, "POST", body),
  delete: (id: string) => del(`/api/books/${id}`),
  updateSettings: (id: string, patch: Partial<{
    title: string;
    shopify_product_handles: string[];
    gemini_model: string;
    gemini_aspect_ratio: string;
    gemini_image_size: string;
    gemini_thinking_level: "LOW" | "MEDIUM" | "HIGH";
    preview_settings: PreviewSettings;
    scene_settings: SceneSettings;
  }>) => send<{ book: Book }>(`/api/books/${id}`, "PATCH", patch),

  // Preview-Assets: "cover" = @img1 der Generierung, "overlay" = wird danach
  // auf das fertige Bild komponiert, "placeholder" = Bild für die Wartezeit.
  uploadPreviewAsset: async (bookId: string, kind: PreviewAssetKind, file: File) => {
    const form = new FormData();
    form.append("image", file);
    const token = getToken();
    const res = await fetch(`${BASE}/api/books/${bookId}/preview-asset/${kind}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      let message = `upload failed: ${res.status}`;
      try {
        const body = await res.json();
        if (body?.error) message = body.error;
      } catch { /* ignore parse failures */ }
      throw new Error(message);
    }
    return res.json();
  },
  deletePreviewAsset: (bookId: string, kind: PreviewAssetKind) =>
    del(`/api/books/${bookId}/preview-asset/${kind}`),
  previewAssetUrl: (bookId: string, kind: PreviewAssetKind) =>
    `${BASE}/api/books/${bookId}/preview-asset/${kind}`,

  /** Rendert Cover + Overlay + Beispielname serverseitig — echte WYSIWYG-Vorschau. */
  nameSample: async (bookId: string, body: { name: string; layout: PreviewNameLayout }) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/books/${bookId}/preview-name-sample`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let message = `Vorschau fehlgeschlagen: ${res.status}`;
      try {
        const err = await res.json();
        if (err?.error) message = err.error;
      } catch { /* ignore parse failures */ }
      throw new Error(message);
    }
    return URL.createObjectURL(await res.blob());
  },

  // Scenes
  createScene: (bookId: string, body: {
    index: number;
    name: string;
    csv_column: string;
    kind?: "generated" | "fixed";
    prompt?: string;
    scene_emotion?: string | null;
    order_index?: number;
    enabled?: boolean;
    gemini_overrides?: Record<string, unknown> | null;
    scene_overrides?: SceneOverrides | null;
  }) => send<{ scene: BookScene }>(`/api/books/${bookId}/scenes`, "POST", body),
  updateScene: (sceneId: string, patch: {
    index?: number;
    name?: string;
    csv_column?: string;
    kind?: "generated" | "fixed";
    prompt?: string;
    scene_emotion?: string | null;
    order_index?: number;
    enabled?: boolean;
    photo_outfit_only?: boolean;
    gemini_overrides?: Record<string, unknown> | null;
    scene_overrides?: SceneOverrides | null;
  }) => send<{ scene: BookScene }>(`/api/books/scenes/${sceneId}`, "PATCH", patch),
  deleteScene: (sceneId: string) => del(`/api/books/scenes/${sceneId}`),

  uploadSceneImage: async (sceneId: string, file: File, kind: "base" | "fixed") => {
    const form = new FormData();
    form.append("image", file);
    const token = getToken();
    const res = await fetch(`${BASE}/api/books/scenes/${sceneId}/image?kind=${kind}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    return res.json();
  },
  deleteSceneImage: (sceneId: string, kind: "base" | "fixed") =>
    del(`/api/books/scenes/${sceneId}/image/${kind}`),
  sceneImageUrl: (sceneId: string, kind: "base" | "fixed") =>
    `${BASE}/api/books/scenes/${sceneId}/image/${kind}`,

  // Titles — title_number is auto-allocated from a Postgres sequence (server-side)
  createTitle: (bookId: string, body: {
    binding: "SC" | "HC";
    status?: BookTitle["status"];
    height_mm?: number | null;
    width_mm?: number | null;
    total_extent?: number | null;
    colour_pages?: number | null;
    colour_pages_position?: string | null;
  }) => send<{ title: BookTitle }>(`/api/books/${bookId}/titles`, "POST", body),
  updateTitle: (titleNumber: string, patch: {
    status?: BookTitle["status"];
    height_mm?: number | null;
    width_mm?: number | null;
    total_extent?: number | null;
    colour_pages?: number | null;
    colour_pages_position?: string | null;
  }) => send<{ title: BookTitle }>(`/api/books/titles/${encodeURIComponent(titleNumber)}`, "PATCH", patch),
  deleteTitle: (titleNumber: string) => del(`/api/books/titles/${encodeURIComponent(titleNumber)}`),
};

export async function fetchAuthBlob(url: string): Promise<string> {
  const token = getToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`blob fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
